import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from 'src/app/store/auth/auth.state';

/** Stable hash for company ids when `custom.demoDataSeed` is not set yet. */
export function hashDemoSeedString(value: string): number {
  let h = 0;
  for (const c of value) {
    h = Math.imul(31, h) + (c.codePointAt(0) ?? 0);
  }
  return Math.abs(h) || 1;
}

/** Tiny PRNG: same seed → same sequence of [0, 1) floats. */
function mulberry32(seed: number): () => number {
  /* eslint-disable no-bitwise */
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  /* eslint-enable no-bitwise */
}

/**
 * Resolves the company demo seed and exposes scoped PRNG helpers for mock dashboards.
 * Root seed: `companySettings.custom.demoDataSeed`, else {@link hashDemoSeedString}(company id).
 */
@Injectable({
  providedIn: 'root',
})
export class DemoSeedService {
  private cachedCompanyId: string | undefined;
  private cachedRootSeed: number | undefined;

  constructor(private readonly store: Store) {}

  /** Root seed for the signed-in company; stable across refreshes for the same company. */
  getRootSeed(): number {
    const company = this.store.selectSnapshot(AuthState.company);
    const companyId = company?.id ?? 'demo-fallback';

    if (this.cachedCompanyId === companyId && this.cachedRootSeed !== undefined) {
      return this.cachedRootSeed;
    }

    const customSeed = company?.companySettings?.custom?.demoDataSeed;
    if (typeof customSeed === 'number' && Number.isFinite(customSeed)) {
      this.cachedRootSeed = Math.abs(Math.trunc(customSeed)) || 1;
    } else {
      this.cachedRootSeed = hashDemoSeedString(companyId);
    }

    this.cachedCompanyId = companyId;
    return this.cachedRootSeed;
  }

  /** Clears memoized seed (tests or company switch). */
  resetCache(): void {
    this.cachedCompanyId = undefined;
    this.cachedRootSeed = undefined;
  }

  scopedSeedKey(...scopeParts: (string | number)[]): string {
    return `${this.getRootSeed()}:${scopeParts.map(String).join(':')}`;
  }

  rng(...scopeParts: (string | number)[]): () => number {
    return mulberry32(hashDemoSeedString(this.scopedSeedKey(...scopeParts)));
  }

  float(...scopeParts: (string | number)[]): number {
    return this.rng(...scopeParts)();
  }

  between(min: number, max: number, ...scopeParts: (string | number)[]): number {
    return min + this.float(...scopeParts) * (max - min);
  }

  int(min: number, max: number, ...scopeParts: (string | number)[]): number {
    const lo = Math.ceil(Math.min(min, max));
    const hi = Math.floor(Math.max(min, max));
    if (hi <= lo) {
      return lo;
    }
    return lo + Math.floor(this.float(...scopeParts) * (hi - lo + 1));
  }

  pickIndex(length: number, ...scopeParts: (string | number)[]): number {
    if (length <= 0) {
      return 0;
    }
    return this.int(0, length - 1, ...scopeParts);
  }
}
